// Catalog.ID Client — Kit Doc-Definition (pure)
// ============================================================================
// The pdfmake document for the single print job of registration-restructure
// §5, minus any bundled asset: the letterhead background is passed in, so
// tooling outside the bundler (sample renders) can build the same document
// from the design/ files directly. booklet.ts binds the real letterhead.

import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { LETTERHEAD_PAGE_MARGINS_A4, type LetterheadBackground } from '../letterhead-background';
import {
    BOOKLET_STYLES, BOOKLET_TITLE, FOOTER_FONT_SIZE, FOOTER_TOP_MARGIN, shortKitTitle,
} from './shared';

/** An extra sheet appended after the master set — a printed duplicate of one of
 *  its pages, outside the "page n of n" numbering and carrying its own footer.
 *  Today only the recovery sheets produce these (`buildRecoveryCardCopySheets`). */
export interface CopySheet {
    content: Content[];
    footer: string;
}

/** Everything the assembler needs beyond the pages themselves. All optional:
 *  absent means the pre-kit behaviour — the personal title, no appended terms,
 *  the same letterhead on page 1 as everywhere else. */
export interface BookletDefinitionOptions {
    /** Printed name of the kit — footer line and PDF title. */
    title?: string;
    /** Numbered pages of the WHOLE document, terms included (KitLayout's
     *  `totalPages`). Defaults to the master page count, which is what a kit
     *  without appended terms has. */
    totalPages?: number;
    /** Artwork for page 1 only. The designer's cover SVG goes here; the
     *  default is the ordinary letterhead, so the cover is never blank. */
    coverBackground?: LetterheadBackground;
}

/** Uniform A4, letterhead margins, "page n of n" footer (right-aligned, clear
 *  of the letterhead's bottom-center logo); every page after the first starts
 *  on a fresh sheet. Pages may be separated, so the background must carry the
 *  full letterhead on every page (letterheadEveryPage).
 *
 *  `copySheets` ride AFTER the master pages and are deliberately NOT numbered:
 *  the master set is the document — "page 4 of 15" must keep meaning what it
 *  said when the owner counted the sheets — so each copy prints its own footer
 *  line instead ("Account Recovery Sheet C — copy 1 of 2"). The footer callback
 *  therefore maps by position: the first `pages.length` sheets are the set.
 *
 *  The Membership Agreement is NOT part of this document: it is appended
 *  afterwards from a pre-rendered asset (kit-merge.ts). `options.totalPages`
 *  is how the footer here counts those pages before they exist. */
export function buildBookletDefinitionWith(
    pages: Content[][],
    background: LetterheadBackground,
    copySheets: CopySheet[] = [],
    options: BookletDefinitionOptions = {},
): TDocumentDefinitions {
    const masterCount = pages.length;
    const title = options.title ?? BOOKLET_TITLE;
    const totalPages = options.totalPages ?? masterCount;
    const cover = options.coverBackground;
    const all: Content[][] = [...pages, ...copySheets.map(s => s.content)];
    const content: Content[] = all.map((page, i) =>
        i === 0 ? { stack: page } : { stack: page, pageBreak: 'before' as const });
    return {
        pageSize: 'A4',
        pageMargins: LETTERHEAD_PAGE_MARGINS_A4,
        info: { title },
        background: cover
            ? (currentPage, pageSize) => (currentPage === 1 ? cover : background)(currentPage, pageSize)
            : background,
        // The footer line's baseline sits on the letterhead's "catalog.org"
        // row — FOOTER_TOP_MARGIN is derived from the artwork in shared.ts —
        // and drops the brand prefix so the longest title clears the lockup.
        footer: (currentPage: number) => ({
            text: currentPage <= masterCount
                ? `${shortKitTitle(title)} — page ${currentPage} of ${totalPages}`
                : copySheets[currentPage - masterCount - 1]?.footer ?? '',
            alignment: 'right',
            margin: [60, FOOTER_TOP_MARGIN, 60, 0],
            fontSize: FOOTER_FONT_SIZE,
        }),
        content,
        styles: BOOKLET_STYLES,
        defaultStyle: { fontSize: 10 },
    };
}
